import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { scorePassword } from '@/utils/passwordStrength';

interface SetPasswordFieldsProps {
  password: string;
  confirm: string;
  onPasswordChange: (value: string) => void;
  onConfirmChange: (value: string) => void;
}

const BAR_COLOURS = ['bg-gray-200', 'bg-red-400', 'bg-amber-400', 'bg-green-500'];

/** Shared password + confirm UI with show/hide eye and a lightweight strength meter. */
const SetPasswordFields = ({ password, confirm, onPasswordChange, onConfirmChange }: SetPasswordFieldsProps) => {
  const [show, setShow] = useState(false);
  const strength = scorePassword(password);
  const mismatch = confirm.length > 0 && confirm !== password;

  return (
    <div className="flex flex-col gap-4">
      <Field>
        <FieldLabel htmlFor="new-password">New password</FieldLabel>
        <div className="relative">
          <Input
            id="new-password"
            type={show ? 'text' : 'password'}
            className="bg-white dark:text-gray-900 text-lg pr-10"
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            aria-describedby="password-strength"
          />
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="absolute inset-y-0 right-2 flex items-center text-gray-500"
            aria-label={show ? 'Hide password' : 'Show password'}
          >
            {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        </div>
        <div id="password-strength" className="mt-1">
          <div className={`h-1 rounded ${BAR_COLOURS[strength.score]}`} style={{ width: `${(strength.score / 3) * 100 || 8}%` }} />
          <span className="text-xs text-gray-500">{password.length === 0 ? 'At least 8 characters' : strength.label}</span>
        </div>
      </Field>

      <Field>
        <FieldLabel htmlFor="confirm-password">Confirm password</FieldLabel>
        <Input
          id="confirm-password"
          type={show ? 'text' : 'password'}
          className="bg-white dark:text-gray-900 text-lg"
          value={confirm}
          onChange={(e) => onConfirmChange(e.target.value)}
        />
        {mismatch && <span className="text-xs text-red-600">Passwords don&apos;t match</span>}
      </Field>
    </div>
  );
};

export default SetPasswordFields;
